import { getBaseUrl } from '../..'
import { fetchApi } from '../fetchApi'

export enum PermissionRequestStatusT {
  CREATED = 'CREATED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
}

export interface PermissionRequestBaseT {
  id: string
  requesterId: string
  requesterEmail: string
  reason: string
  expirationDate: string
  status: PermissionRequestStatusT
}

export interface CaseDeletionRetentionPolicyApplyPermissionRequestT
  extends PermissionRequestBaseT {
  type: 'caseDeletionRetentionPolicyApplyPermissionRequest'
  editorWorkflowId: string
  expirationTimeInSeconds: number
  caseDeletionRetentionPolicyEnabled: boolean
  applyToAll: boolean
}

export interface WorkflowGovernanceExportPermissionRequestT
  extends PermissionRequestBaseT {
  type: 'workflowGovernanceExportPermissionRequest'
}

export interface WorkflowAccessGovernanceExportPermissionRequestT
  extends PermissionRequestBaseT {
  type: 'workflowAccessGovernanceExportPermissionRequest'
}

export type PermissionRequestT =
  | CaseDeletionRetentionPolicyApplyPermissionRequestT
  | WorkflowGovernanceExportPermissionRequestT
  | WorkflowAccessGovernanceExportPermissionRequestT

const staticSaveRetentionSettingsParams = {
  jsonType: 'caseDeletionRetentionPolicyApplyPermissionRequestRequest',
  type: 'caseDeletionRetentionPolicyApplyPermissionRequest',
} as const

export type PermissionRequestTypeT = PermissionRequestT['type']

export type RequestSaveRetentionSettingsParamsT = {
  editorWorkflowId: string
  applyToAll: boolean
  isCaseDeletionRetentionPolicyEnabled: boolean
  caseDeletionRetentionPolicyDurationSeconds: number
  type: 'caseDeletionRetentionPolicyApplyPermissionRequest'
  jsonType: 'caseDeletionRetentionPolicyApplyPermissionRequestRequest'
  requesterId: string
  reason: string
}

const getPermissionRequestBasePath = () => `${getBaseUrl()}/permission-requests`

export const listPermissionRequests = async () =>
  await fetchApi<PermissionRequestT[]>(getPermissionRequestBasePath())

export const getPermissionRequest = async (permissionRequestId: string) =>
  await fetchApi<PermissionRequestT>(
    `${getPermissionRequestBasePath()}/${permissionRequestId}`
  )

export const processPermissionRequest = async (
  permissionRequestId: string,
  accepted: boolean,
  reason?: string
) => {
  await fetchApi<void>(
    `${getPermissionRequestBasePath()}/${permissionRequestId}/decision`,
    {
      method: 'POST',
      body: JSON.stringify({
        permissionRequestId,
        accepted,
        reason,
      }),
    }
  )

  return accepted
}

export const fetchActivePermissionRequest = ({
  workflowId,
  options,
}: {
  workflowId: string
  options?: RequestInit
}): Promise<PermissionRequestT> =>
  fetchApi(`${getPermissionRequestBasePath()}/active/${workflowId}`, options)

export const createPermissionRequestForRetentionSettings = (
  settings: Omit<RequestSaveRetentionSettingsParamsT, 'jsonType' | 'type'>
) => {
  const data: RequestSaveRetentionSettingsParamsT = {
    ...settings,
    ...staticSaveRetentionSettingsParams,
  }

  return fetchApi<PermissionRequestT>(`${getBaseUrl()}/permission-requests`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
